import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { format, parse } from "date-fns";
import {
  ArrowLeft,
  Mail,
  Sparkles,
  CalendarPlus,
  LogOut,
  ExternalLink,
  Loader2,
  CheckCircle2,
  Clock,
  AlertCircle,
  CreditCard,
  Calendar as CalendarIcon,
} from "lucide-react";
import { useSeo, SITE } from "@/hooks/useSeo";
import { TIME_WINDOW_OPTIONS } from "@/components/home/constants";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ggg_member_token";

const MEMBER_SERVICES = [
  "Gutter Cleaning",
  "Gutter Cover Installation",
  "Gutter Repairs",
  "Gutter Reseal Service",
  "Window Screen Replacement",
  "Inspection / Walk-around",
];

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmtDate(d) {
  if (!d) return "—";
  try {
    if (typeof d === "number") return new Date(d * 1000).toLocaleDateString();
    return new Date(d).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return String(d);
  }
}

function statusBadge(status) {
  const map = {
    requested: {
      label: "Requested",
      cls: "bg-[#F5E9A5]/20 text-[#FFD86B] border-[#FFD86B]/40",
      icon: Clock,
    },
    scheduled: {
      label: "Scheduled",
      cls: "bg-[#5FBE1F]/15 text-[#5FBE1F] border-[#5FBE1F]/40",
      icon: CalendarIcon,
    },
    completed: {
      label: "Completed",
      cls: "bg-white/10 text-white/70 border-white/20",
      icon: CheckCircle2,
    },
    cancelled: {
      label: "Cancelled",
      cls: "bg-red-500/10 text-red-400 border-red-400/30",
      icon: AlertCircle,
    },
  };
  const m = map[status] || map.requested;
  const Icon = m.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-sm border ${m.cls}`}
    >
      <Icon className="w-3 h-3" />
      {m.label}
    </span>
  );
}

// ---------------- Login Form (request magic link) ----------------
function LoginForm() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await axios.post(`${API}/member/request-link`, { email });
      setSent(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not send link. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div
        className="w-full max-w-md bg-white/[0.03] border border-white/10 rounded-sm p-8"
        data-testid="member-link-sent"
      >
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#5FBE1F] mb-3">
          <Sparkles className="w-4 h-4" />
          Check your inbox
        </div>
        <h1 className="font-display text-3xl text-white mb-3">
          Login link sent
        </h1>
        <p className="text-sm text-white/70 leading-relaxed">
          If <span className="text-white font-semibold">{email}</span> is tied
          to an active membership, we just emailed you a one-tap login link.
          It expires in 15 minutes.
        </p>
        <p className="mt-5 text-xs text-white/40">
          Didn&apos;t get it? Check spam, or text Mike at (712) 310-9514 and he&apos;ll
          confirm your account is set up.
        </p>
        <button
          onClick={() => {
            setSent(false);
            setEmail("");
          }}
          className="mt-6 text-sm text-white/60 hover:text-white underline underline-offset-2"
          data-testid="member-link-try-again"
        >
          Use a different email
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="w-full max-w-md bg-white/[0.03] border border-white/10 rounded-sm p-8"
      data-testid="member-login-form"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#5FBE1F] mb-3">
        <Sparkles className="w-4 h-4" />
        Member Portal
      </div>
      <h1 className="font-display text-3xl text-white mb-2">
        Sign in by email
      </h1>
      <p className="text-sm text-white/60 mb-6 leading-relaxed">
        Enter the email tied to your membership. We&apos;ll send a magic link — no
        password needed.
      </p>
      <label className="block text-xs font-bold uppercase tracking-[0.18em] text-white/60 mb-2">
        Email
      </label>
      <div className="relative">
        <Mail className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="email"
          required
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="w-full bg-black border border-white/10 pl-9 pr-4 py-3 rounded-sm text-white placeholder:text-white/30 focus:border-[#5FBE1F] outline-none"
          data-testid="member-email-input"
        />
      </div>
      {error && (
        <p className="mt-3 text-sm text-red-400" data-testid="member-login-error">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={busy || !email}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#5FBE1F] text-black font-bold py-3 rounded-sm hover:bg-[#4A9818] hover:text-white disabled:opacity-50 transition-colors"
        data-testid="member-login-submit"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
        {busy ? "Sending…" : "Email me a login link"}
      </button>
      <p className="mt-6 text-xs text-white/40 leading-relaxed">
        Not a member yet?{" "}
        <Link
          to="/plans"
          className="text-[#5FBE1F] underline underline-offset-2 hover:text-white"
          data-testid="member-cta-plans"
        >
          See membership plans →
        </Link>
      </p>
    </form>
  );
}

// ---------------- Pay for a service (custom Stripe Checkout w/ coupon) ----------------
function PayCatalog({ token, tier }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/member/pay-catalog`, { headers: authHeaders(token) })
      .then((res) => setData(res.data))
      .catch((e) => setError(e?.response?.data?.detail || "Could not load services"))
      .finally(() => setLoading(false));
  }, [token]);

  async function pay(serviceId) {
    setPayingId(serviceId);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/member/checkout-session`,
        { service_id: serviceId },
        { headers: authHeaders(token) }
      );
      window.location.href = res.data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start checkout");
      setPayingId(null);
    }
  }

  if (loading) {
    return (
      <div
        className="bg-white/[0.03] border border-white/10 rounded-sm p-6 text-sm text-white/60 flex items-center gap-2"
        data-testid="member-pay-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading pricing…
      </div>
    );
  }
  if (!data) return null;

  const hasDiscount = data.discount_pct > 0;

  return (
    <section
      className="bg-white/[0.03] border border-white/10 rounded-sm p-6"
      data-testid="member-pay-catalog"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#5FBE1F] mb-3">
        <CreditCard className="w-4 h-4" />
        Pay for a service
      </div>
      <h2 className="font-display text-2xl text-white mb-1">
        Pay online — discount auto-applied
      </h2>
      <p className="text-sm text-white/60 mb-5 leading-relaxed">
        {hasDiscount
          ? `Your ${tier} ${data.discount_pct}% discount is applied automatically at checkout (coupon ${data.coupon}).`
          : `Choose a service below to pay securely via Stripe.`}
      </p>

      <ul className="space-y-3">
        {data.services.map((s) => (
          <li
            key={s.id}
            className="border border-white/10 rounded-sm p-4 sm:p-5 hover:border-[#5FBE1F]/60 transition-colors"
            data-testid={`member-pay-item-${s.id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <h3 className="font-display text-xl text-white">{s.name}</h3>
                  {hasDiscount ? (
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-white/40 line-through">
                        ${s.base_amount.toFixed(2)}
                      </span>
                      <span className="font-display text-2xl text-[#5FBE1F]">
                        ${s.discounted_amount.toFixed(2)}
                      </span>
                    </div>
                  ) : (
                    <span className="font-display text-2xl text-[#5FBE1F]">
                      ${s.base_amount.toFixed(2)}
                    </span>
                  )}
                </div>
                {hasDiscount && s.savings > 0 && (
                  <div className="mt-1 text-xs text-[#5FBE1F]">
                    You save ${s.savings.toFixed(2)} with {data.coupon}
                  </div>
                )}
                <p className="mt-2 text-sm text-white/60 leading-relaxed">
                  {s.description}
                </p>
              </div>
              <button
                onClick={() => pay(s.id)}
                disabled={payingId === s.id}
                className="inline-flex items-center gap-2 bg-[#5FBE1F] text-black font-bold px-5 py-2.5 rounded-sm hover:bg-[#4A9818] hover:text-white disabled:opacity-50 transition-colors text-sm"
                data-testid={`member-pay-cta-${s.id}`}
              >
                {payingId === s.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4" />
                )}
                {payingId === s.id ? "Opening…" : "Pay now"}
              </button>
            </div>
          </li>
        ))}
      </ul>
      {error && (
        <p className="mt-4 text-sm text-red-400" data-testid="member-pay-error">
          {error}
        </p>
      )}
      <p className="mt-5 text-xs text-white/40">
        Paying a custom amount (repairs, partial visits, etc.)? Text Mike at
        (712) 310-9514 and he&apos;ll send an invoice with your discount.
      </p>
    </section>
  );
}

// ---------------- Schedule Service Form ----------------
function ScheduleForm({ token, onScheduled }) {
  const [serviceType, setServiceType] = useState("");
  const [preferredDate, setPreferredDate] = useState("");
  const [timeWindow, setTimeWindow] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (!serviceType || !preferredDate || !timeWindow) {
      setError("Pick a service, a date, and a time window.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await axios.post(
        `${API}/member/schedule-service`,
        {
          service_type: serviceType,
          preferred_date: preferredDate,
          preferred_time_window: timeWindow,
          notes: notes || null,
        },
        { headers: authHeaders(token) }
      );
      setSuccess(true);
      setServiceType("");
      setPreferredDate("");
      setTimeWindow("");
      setNotes("");
      onScheduled && onScheduled();
      setTimeout(() => setSuccess(false), 4000);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not submit request.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="bg-white/[0.03] border border-white/10 rounded-sm p-6"
      data-testid="member-schedule-form"
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#5FBE1F] mb-3">
        <CalendarPlus className="w-4 h-4" />
        Schedule a Service
      </div>
      <h2 className="font-display text-2xl text-white mb-2">
        Request your next visit
      </h2>
      <p className="text-sm text-white/60 mb-6 leading-relaxed">
        Pick a service and a window. Mike will confirm by text within a day or
        two and lock it onto the schedule.
      </p>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="block text-xs font-bold uppercase tracking-[0.18em] text-white/60 mb-2">
            Service
          </label>
          <Select value={serviceType} onValueChange={setServiceType}>
            <SelectTrigger
              className="bg-black border-white/10 text-white rounded-sm h-11 focus:ring-[#5FBE1F]"
              data-testid="member-service-type"
            >
              <SelectValue placeholder="Choose a service" />
            </SelectTrigger>
            <SelectContent>
              {MEMBER_SERVICES.map((s) => (
                <SelectItem
                  key={s}
                  value={s}
                  data-testid={`member-service-option-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.18em] text-white/60 mb-2">
            Preferred date
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="member-preferred-date"
                className="w-full h-11 px-3 flex items-center justify-between bg-black border border-white/10 rounded-sm text-left text-sm text-white hover:border-[#5FBE1F] focus:outline-none focus:ring-2 focus:ring-[#5FBE1F]"
              >
                <span className={preferredDate ? "" : "text-white/40"}>
                  {preferredDate
                    ? format(parse(preferredDate, "yyyy-MM-dd", new Date()), "EEE, MMM d, yyyy")
                    : "Pick a day"}
                </span>
                <CalendarIcon className="w-4 h-4 text-white/60" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={
                  preferredDate
                    ? parse(preferredDate, "yyyy-MM-dd", new Date())
                    : undefined
                }
                onSelect={(d) => setPreferredDate(d ? format(d, "yyyy-MM-dd") : "")}
                disabled={(d) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return d < today;
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-[0.18em] text-white/60 mb-2">
            Preferred time
          </label>
          <Select value={timeWindow} onValueChange={setTimeWindow}>
            <SelectTrigger
              className="bg-black border-white/10 text-white rounded-sm h-11 focus:ring-[#5FBE1F]"
              data-testid="member-time-window"
            >
              <SelectValue placeholder="Pick a window" />
            </SelectTrigger>
            <SelectContent>
              {TIME_WINDOW_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  data-testid={`member-time-option-${opt.value}`}
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="sm:col-span-2">
          <label className="block text-xs font-bold uppercase tracking-[0.18em] text-white/60 mb-2">
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Gate code, dog in yard, specific gutter section, etc."
            className="w-full bg-black border border-white/10 px-3 py-2 rounded-sm text-white placeholder:text-white/30 focus:border-[#5FBE1F] outline-none text-sm"
            data-testid="member-notes"
          />
        </div>
      </div>

      {error && (
        <p className="mt-4 text-sm text-red-400" data-testid="member-schedule-error">
          {error}
        </p>
      )}
      {success && (
        <p
          className="mt-4 text-sm text-[#5FBE1F] flex items-center gap-2"
          data-testid="member-schedule-success"
        >
          <CheckCircle2 className="w-4 h-4" />
          Request submitted — Mike will text you to confirm.
        </p>
      )}

      <button
        type="submit"
        disabled={busy}
        className="mt-5 inline-flex items-center gap-2 bg-[#5FBE1F] text-black font-bold px-6 py-3 rounded-sm hover:bg-[#4A9818] hover:text-white disabled:opacity-50 transition-colors"
        data-testid="member-schedule-submit"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
        {busy ? "Submitting…" : "Request this service"}
      </button>
    </form>
  );
}

// ---------------- Dashboard ----------------
function Dashboard({ token, onLogout }) {
  const [me, setMe] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = authHeaders(token);
      const [meRes, svcRes] = await Promise.all([
        axios.get(`${API}/member/me`, { headers }),
        axios.get(`${API}/member/services`, { headers }),
      ]);
      setMe(meRes.data);
      setServices(svcRes.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        onLogout();
      } else {
        setError(e?.response?.data?.detail || "Could not load your account.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  async function openPortal() {
    setPortalBusy(true);
    setPortalError(null);
    try {
      const res = await axios.post(
        `${API}/member/billing-portal`,
        {},
        { headers: authHeaders(token) }
      );
      window.location.href = res.data.url;
    } catch (e) {
      setPortalError(
        e?.response?.data?.detail ||
          "Couldn't open billing portal. Try again in a moment."
      );
      setPortalBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-white/60" data-testid="member-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading your account…
      </div>
    );
  }

  if (error || !me) {
    return (
      <div
        className="bg-white/[0.03] border border-red-400/30 rounded-sm p-6 text-red-400"
        data-testid="member-load-error"
      >
        <div className="flex items-center gap-2 mb-2">
          <AlertCircle className="w-4 h-4" />
          {error || "We couldn't load your membership."}
        </div>
        <button
          onClick={onLogout}
          className="text-xs underline underline-offset-2 hover:text-white"
        >
          Sign out and try again
        </button>
      </div>
    );
  }

  const upcoming = services.filter((s) => s.status === "requested" || s.status === "scheduled");
  const past = services.filter((s) => s.status === "completed" || s.status === "cancelled");

  return (
    <div className="space-y-8" data-testid="member-dashboard">
      {/* Membership card */}
      <div
        className="bg-gradient-to-br from-[#5FBE1F]/10 via-white/[0.02] to-transparent border border-[#5FBE1F]/30 rounded-sm p-6 sm:p-8"
        data-testid="member-membership-card"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-[#5FBE1F] mb-2">
              Active Membership
            </div>
            <div className="font-display text-3xl sm:text-4xl text-white">
              {me.plan_name || "Member"}
            </div>
            <div className="mt-1 text-sm text-white/60">
              {me.name ? `${me.name} · ` : ""}
              {me.email}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-[0.18em] text-white/40">
              Member discount
            </div>
            <div className="font-display text-4xl text-[#5FBE1F]">
              {me.discount_pct || 0}%
            </div>
            <div className="text-xs text-white/40">off repairs & guards</div>
          </div>
        </div>

        <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-white/40 mb-1">
              Billing
            </div>
            <div className="text-white">
              ${me.amount?.toFixed?.(2) || me.amount} / {me.interval}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-white/40 mb-1">
              {me.cancel_at_period_end ? "Ends on" : "Renews on"}
            </div>
            <div className="text-white">{fmtDate(me.renews_at)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-white/40 mb-1">
              Tier
            </div>
            <div className="text-white">{me.tier || "Member"}</div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <button
            onClick={openPortal}
            disabled={portalBusy}
            className="inline-flex items-center gap-2 bg-white text-black font-bold px-5 py-2.5 rounded-sm hover:bg-[#5FBE1F] disabled:opacity-50 transition-colors text-sm"
            data-testid="member-billing-portal-btn"
          >
            {portalBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
            Manage billing in Stripe
          </button>
          <a
            href="sms:+17123109514?body=Member"
            className="inline-flex items-center gap-2 border border-white/20 text-white px-5 py-2.5 rounded-sm hover:border-[#5FBE1F] hover:text-[#5FBE1F] text-sm"
            data-testid="member-text-mike"
          >
            Text Mike
          </a>
        </div>
        {portalError && (
          <p className="mt-3 text-xs text-red-400" data-testid="member-portal-error">
            {portalError}
          </p>
        )}
        {me.discount_pct > 0 && (
          <p className="mt-5 text-xs text-white/50 leading-relaxed">
            Your {me.discount_pct}% member discount is applied automatically
            on every Stripe checkout below. For custom invoices, text Mike with
            &quot;Member discount&quot; and he&apos;ll knock it off.
          </p>
        )}
      </div>

      {/* Pay catalog with auto-discount */}
      <PayCatalog token={token} tier={me.tier} />

      {/* Schedule form */}
      <ScheduleForm token={token} onScheduled={loadAll} />

      {/* Upcoming / Requested */}
      <section data-testid="member-upcoming-section">
        <h2 className="font-display text-2xl text-white mb-4">
          Upcoming & requested
        </h2>
        {upcoming.length === 0 ? (
          <div className="bg-white/[0.02] border border-white/10 rounded-sm p-6 text-sm text-white/50">
            Nothing scheduled. Request a visit above and Mike will confirm.
          </div>
        ) : (
          <ul className="space-y-3">
            {upcoming.map((s) => (
              <li
                key={s.id}
                className="bg-white/[0.03] border border-white/10 rounded-sm p-4 sm:p-5 flex flex-wrap items-start justify-between gap-3"
                data-testid={`member-service-${s.id}`}
              >
                <div>
                  <div className="font-bold text-white">{s.service_type}</div>
                  <div className="text-sm text-white/60 mt-0.5">
                    {s.status === "scheduled" && s.scheduled_date
                      ? `Confirmed for ${s.scheduled_date}${s.scheduled_time_window ? ` · ${s.scheduled_time_window}` : ""}`
                      : `Requested for ${s.requested_date}${s.requested_time_window ? ` · ${s.requested_time_window}` : ""}`}
                  </div>
                  {s.notes && (
                    <div className="text-xs text-white/40 mt-1 italic">&quot;{s.notes}&quot;</div>
                  )}
                </div>
                {statusBadge(s.status)}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Past */}
      <section data-testid="member-past-section">
        <h2 className="font-display text-2xl text-white mb-4">Service history</h2>
        {past.length === 0 ? (
          <div className="bg-white/[0.02] border border-white/10 rounded-sm p-6 text-sm text-white/50">
            No completed visits on record yet.
          </div>
        ) : (
          <ul className="space-y-3">
            {past.map((s) => (
              <li
                key={s.id}
                className="bg-white/[0.02] border border-white/10 rounded-sm p-4 sm:p-5 flex flex-wrap items-start justify-between gap-3"
                data-testid={`member-past-${s.id}`}
              >
                <div>
                  <div className="font-bold text-white">{s.service_type}</div>
                  <div className="text-sm text-white/60 mt-0.5">
                    {s.completed_at
                      ? `Completed ${fmtDate(s.completed_at)}`
                      : `Requested ${s.requested_date || fmtDate(s.created_at)}`}
                  </div>
                </div>
                {statusBadge(s.status)}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ---------------- Main page ----------------
export default function Member() {
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState(null);

  useSeo({
    title: "Member Portal — Glenwood Gutter Guy",
    description:
      "Sign in to your Glenwood Gutter Guy membership: schedule services, view past visits, and manage your subscription.",
    url: `${SITE.url}/member`,
    type: "website",
  });

  // Magic link verify on /member/verify?token=...
  useEffect(() => {
    if (location.pathname !== "/member/verify") return;
    const params = new URLSearchParams(location.search);
    const magicToken = params.get("token");
    if (!magicToken) {
      setVerifyError("Missing token in link.");
      return;
    }
    setVerifying(true);
    setVerifyError(null);
    axios
      .post(`${API}/member/verify`, { token: magicToken })
      .then((res) => {
        localStorage.setItem(TOKEN_KEY, res.data.token);
        setToken(res.data.token);
        navigate("/member", { replace: true });
      })
      .catch((e) => {
        setVerifyError(
          e?.response?.data?.detail || "Login link is invalid or expired."
        );
      })
      .finally(() => setVerifying(false));
  }, [location, navigate]);

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }

  // Stripe Checkout return banners (?paid=1 / ?cancelled=1)
  const params = new URLSearchParams(location.search);
  const paid = params.get("paid") === "1";
  const cancelled = params.get("cancelled") === "1";
  useEffect(() => {
    if ((paid || cancelled) && location.pathname === "/member") {
      const t = setTimeout(() => navigate("/member", { replace: true }), 6000);
      return () => clearTimeout(t);
    }
  }, [paid, cancelled, location.pathname, navigate]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Top bar */}
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white"
            data-testid="member-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <div className="text-xs uppercase tracking-[0.24em] text-white/50">
            Member Portal
          </div>
          {token ? (
            <button
              onClick={logout}
              className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white"
              data-testid="member-logout"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </button>
          ) : (
            <span className="w-[80px]" /> // spacer for symmetry
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {verifying && (
          <div
            className="flex items-center gap-2 text-white/70 mb-6"
            data-testid="member-verifying"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            Signing you in…
          </div>
        )}
        {verifyError && !token && (
          <div
            className="bg-red-400/10 border border-red-400/30 rounded-sm p-4 mb-6 text-sm text-red-300"
            data-testid="member-verify-error"
          >
            {verifyError}
          </div>
        )}
        {paid && token && (
          <div
            className="bg-[#5FBE1F]/10 border border-[#5FBE1F]/40 rounded-sm p-4 mb-6 text-sm text-[#5FBE1F] flex items-center gap-2"
            data-testid="member-paid-banner"
          >
            <CheckCircle2 className="w-4 h-4" />
            Payment received — Stripe just emailed you a receipt. Mike will be in touch.
          </div>
        )}
        {cancelled && token && (
          <div
            className="bg-white/[0.04] border border-white/10 rounded-sm p-4 mb-6 text-sm text-white/70"
            data-testid="member-cancelled-banner"
          >
            Checkout cancelled — nothing was charged. Want to try a different service?
          </div>
        )}

        {token ? (
          <Dashboard token={token} onLogout={logout} />
        ) : (
          <div className="flex justify-center pt-4">
            <LoginForm />
          </div>
        )}
      </main>
    </div>
  );
}
